import type { CartLine } from '../../store/cart';
import { useLang } from '../../lib/language';
import { XIcon } from '../../components/icons';

/**
 * "Do these go back on the shelf?" — the confirmation the legacy POS shows
 * before a return is submitted.
 *
 * The legacy builds exactly this list in `PreCancelOrder()`
 * (`order_events_definitions.js`): every cart line whose item carries
 * `Inv_Items.RestockOnReturn`, one row each — name, quantity, and a checkbox
 * that starts TICKED. On confirm it walks `.restockCheck` and, for every box
 * the cashier cleared, stops that line's stock coming back
 * (`if (!this.checked) row.Transaction_move = 0`). Ticked means restock; that
 * polarity is the ERP's own, and `POS_CancelOrder.sql` agrees with it
 * (`CASE WHEN RestockOnReturn = 0 THEN -1 ELSE 0 END`).
 *
 * Two deliberate differences from the legacy, both improvements we already
 * shipped and are not undoing:
 *
 * - A cleared box writes a real **Code 61 Destruction voucher** rather than
 *   merely zeroing the movement, so the loss is a document with a cost on it
 *   instead of stock that quietly evaporates.
 * - The same decision is also editable inline on the cart line. This dialog and
 *   that checkbox are one piece of state (`CartLine.restock`), so whichever the
 *   cashier touches, the other agrees.
 *
 * Only items the tenant flagged appear. An unflagged item always restocks and
 * is never listed — showing every line would train people to click through it.
 */
export function RestockConfirmDialog(props: {
  lines: CartLine[];
  onToggle: (cartId: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  const { t, lang } = useLang();
  const { lines } = props;
  if (lines.length === 0) return null;

  const nameOf = (l: CartLine): string =>
    (lang === 'ar' ? l.product.nameAr : l.product.name) || l.product.name || l.product.nameAr || '';
  const wasted = lines.filter((l) => l.restock === false).length;

  return (
    <div className="modal-overlay show restock-overlay" onClick={props.onCancel}>
      <div
        className="modal restock-modal"
        role="alertdialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-header-img restock-icon" aria-hidden="true">
            ♻️
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">
              {t({ en: 'Returning to stock?', ar: 'هل تعود إلى المخزون؟' })}
            </div>
            <div className="modal-header-name-ar">
              {t({
                en: 'These items are ticked to go back on the shelf. Clear the tick for anything that cannot be resold — it will be written off as wastage.',
                ar: 'الأصناف التالية محددة للعودة إلى المخزون. أزل التحديد عن أي صنف لا يصلح للبيع — سيتم إعدامه كتوالف.',
              })}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={props.onCancel}
            aria-label={t({ en: 'Close', ar: 'إغلاق' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body">
          <table className="restock-table">
            <thead>
              <tr>
                <th>{t({ en: 'Item', ar: 'الصنف' })}</th>
                <th className="num">{t({ en: 'Qty', ar: 'الكمية' })}</th>
                <th className="restock-col">{t({ en: 'Back to stock', ar: 'إعادة للمخزون' })}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const restock = l.restock ?? true;
                return (
                  <tr key={l.cartId} className={restock ? undefined : 'is-wastage'}>
                    <td>
                      <div className="restock-name">{nameOf(l)}</div>
                      {!restock && (
                        <div className="restock-meta">
                          {t({
                            en: 'Wastage — a destruction voucher will be issued',
                            ar: 'توالف — سيتم إصدار سند إعدام',
                          })}
                        </div>
                      )}
                    </td>
                    <td className="num">{fmtQty(l.qty)}</td>
                    <td className="restock-col">
                      {/* The whole cell is the hit target: this is pressed on a
                          touch screen at a counter, not with a mouse. */}
                      <label className="restock-check">
                        <input
                          type="checkbox"
                          checked={restock}
                          onChange={() => props.onToggle(l.cartId)}
                        />
                        <span>
                          {restock
                            ? t({ en: 'Restock', ar: 'للمخزون' })
                            : t({ en: 'Wastage', ar: 'توالف' })}
                        </span>
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="modal-footer restock-footer">
          {wasted > 0 && (
            <div className="restock-summary">
              {t({
                en: `${wasted} of ${lines.length} will be written off as wastage`,
                ar: `${wasted} من ${lines.length} سيتم إعدامها كتوالف`,
              })}
            </div>
          )}
          <button type="button" className="btn-secondary-modal" onClick={props.onCancel}>
            {t({ en: 'Back to the cart', ar: 'العودة إلى السلة' })}
          </button>
          <button type="button" className="btn-primary-modal" onClick={props.onConfirm}>
            {t({ en: 'Confirm and continue', ar: 'تأكيد ومتابعة' })}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtQty(n: number): string {
  return Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 3 });
}
